import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import WorkCard from "@/components/feature/WorkCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import SectionHeading from "@/components/base/SectionHeading";
import { works } from "@/mocks/works";

export default function DetailPage() {
  const { id } = useParams();
  const work = works.find((item) => item.id === id);
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!work) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line text-3xl"></i>
          </span>
          <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">
            未找到该影视内容
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            内容可能已下架或链接有误，去首页看看其他精彩作品吧。
          </p>
          <Link
            to="/"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
          >
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = works
    .filter(
      (item) =>
        item.id !== work.id &&
        item.type === work.type &&
        item.genres.some((genre) => work.genres.includes(genre)),
    )
    .slice(0, 5);

  const categoryLabel = work.type === "movie" ? "电影" : "电视剧";
  const infoItems = [
    { label: "上映年份", value: String(work.year) },
    { label: "国家地区", value: work.region },
    { label: "类型", value: work.genres.join(" / ") },
    { label: "导演", value: work.director },
    { label: "主演", value: work.actors.join("、") },
    { label: "时长", value: work.duration },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <title>{`${work.title} - 91看片影视详情`}</title>
      <meta
        name="description"
        content={`${work.title}（${work.year}）${work.region}${work.genres.join(
          "/",
        )}影视详情：${work.synopsis.slice(0, 60)}... 91看片提供剧情简介、评分与相关推荐。`}
      />
      <SiteHeader />

      <main className="flex-1">
        {/* 主图与基本信息 */}
        <section className="relative overflow-hidden bg-foreground-950">
          <div className="absolute inset-0">
            <img
              src={work.cover}
              alt=""
              aria-hidden="true"
              className="h-full w-full scale-110 object-cover object-top opacity-30 blur-xl"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/80 to-foreground-950/60"></div>

          <div className="relative mx-auto max-w-site px-4 py-8 md:px-6 md:py-12">
            <div className="[&_a]:!text-background-300 [&_span]:!text-background-300">
              <Breadcrumb items={[{ label: categoryLabel, to: `/category/${work.type}` }, { label: work.title }]} />
            </div>
            <div className="mt-6 flex flex-col gap-6 sm:flex-row">
              <div className="w-40 shrink-0 sm:w-52">
                <div className="h-[240px] w-full overflow-hidden rounded-lg border border-background-50/10 sm:h-[312px]">
                  <img
                    src={work.cover}
                    alt={`${work.title} 海报`}
                    title={`${work.title} - 91看片高清影视`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 text-background-50">
                <h1 className="font-heading text-2xl font-black md:text-4xl">
                  {work.title}
                </h1>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-background-300">
                  <span className="flex items-center gap-1 rounded bg-accent-500 px-2 py-0.5 font-semibold text-primary-950">
                    <i className="ri-star-fill"></i>
                    {work.rating.toFixed(1)}
                  </span>
                  <span>{work.year}</span>
                  <span className="h-1 w-1 rounded-full bg-background-400"></span>
                  <span>{work.region}</span>
                  <span className="h-1 w-1 rounded-full bg-background-400"></span>
                  <span>{work.duration}</span>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {work.genres.map((genre) => (
                    <span
                      key={genre}
                      className="rounded-full border border-background-50/20 px-3 py-0.5 text-xs text-background-200"
                    >
                      {genre}
                    </span>
                  ))}
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setToast("演示功能：暂未接入真实播放器，播放入口仅供展示。")}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
                  >
                    <i className="ri-play-fill text-lg"></i>
                    在线播放
                  </button>
                  <button
                    type="button"
                    onClick={() => setToast("已加入收藏（演示）。")}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
                  >
                    <i className="ri-bookmark-line"></i>
                    收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-site px-4 py-8 md:px-6">
          {/* 基本信息 */}
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-lg font-bold text-foreground-950">
                剧情简介
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-700">
                {work.synopsis}
              </p>

              <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
                  <i className="ri-information-line text-primary-700"></i>
                  播放入口说明
                </h3>
                <p className="mt-2 text-xs leading-6 text-foreground-500">
                  以下播放平台为本站演示用入口，点击不跳转真实播放页面，实际观看请前往正规授权平台。
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {work.platforms.map((platform) => (
                    <button
                      key={platform}
                      type="button"
                      onClick={() =>
                        setToast(`「${platform}」为演示入口，暂未接入真实播放地址。`)
                      }
                      className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-secondary-200 bg-secondary-50 px-3 py-1.5 text-sm text-secondary-900 transition-colors hover:bg-secondary-100"
                    >
                      <i className="ri-play-circle-line"></i>
                      {platform}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* 信息表 */}
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">
                影片信息
              </h2>
              <dl className="mt-4 space-y-3">
                {infoItems.map((item) => (
                  <div key={item.label} className="flex gap-3 text-sm">
                    <dt className="w-16 shrink-0 text-foreground-500">{item.label}</dt>
                    <dd className="flex-1 text-foreground-800">{item.value}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-5 border-t border-background-200 pt-4">
                <Link
                  to={`/category/${work.type}`}
                  className="flex items-center justify-between text-sm text-primary-700 transition-colors hover:text-accent-600"
                >
                  更多{categoryLabel}
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
              </div>
            </div>
          </div>

          {/* 相关推荐 */}
          {related.length > 0 && (
            <section className="mt-12">
              <SectionHeading
                eyebrow="猜你喜欢"
                title="相关推荐"
                desc={`与《${work.title}》同类型的精彩作品`}
              />
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-5">
                {related.map((item) => (
                  <WorkCard key={item.id} work={item} size="compact" />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[70] flex max-w-[92%] -translate-x-1/2 items-center gap-2 rounded-lg bg-foreground-950 px-4 py-3 text-sm text-background-50 animate-fade-up">
          <i className="ri-error-warning-line text-accent-400"></i>
          {toast}
        </div>
      )}

      <SiteFooter />
    </div>
  );
}